import type { FocusEvent } from 'react';
import { useState } from 'react';
import type { MultiValueProps } from 'react-select';
import { components } from 'react-select';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { CustomSelect, InPortal } from '@gonfalon/launchpad-experimental/CustomSelect';
import type { Member, Team } from '@gonfalon/members/types';
import { useMembers } from '@gonfalon/rest-api/hooks/useMembers';
import { useTeams } from '@gonfalon/rest-api/hooks/useTeams';

import { DEBOUNCE_MS } from '#utils/inputUtils';

import { MemberOption } from './MemberOption';
import { TeamOption } from './TeamOption';

export type SelectMembersAndTeamsProps = {
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange: (selected: SelectMembersAndTeamsOption[]) => void;
  placeholder?: string;
  valueContainerStyles?: React.CSSProperties;
  controlStyles?: React.CSSProperties;
};

export type SelectMembersAndTeamsOption =
  | {
      kind: 'member';
      value: string;
    }
  | { kind: 'team'; value: string };

type MemberCache = { [memberId: string]: Member };
type TeamCache = { [teamKey: string]: Team };

export function SelectMembersAndTeams(props: SelectMembersAndTeamsProps) {
  const [query, setQuery] = useState('');
  const onInputChange = useDebounce(setQuery, DEBOUNCE_MS);

  const [memberCache, setMemberCache] = useState<MemberCache>({});
  const membersQuery = useMembers({ params: { filter: { query }, limit: 10 } });
  if (membersQuery.data && membersQuery.data.items.some((m) => !(m._id in memberCache))) {
    setMemberCache((prev) => ({
      ...prev,
      ...membersQuery.data.items.reduce(
        (newEntries, m) => ({ ...newEntries, [m._id]: m }),
        {} as { [memberId: string]: Member },
      ),
    }));
  }

  const [teamCache, setTeamCache] = useState<TeamCache>({});
  const teamsQuery = useTeams({ params: { filter: { query, nomembers: false }, expand: ['members'], limit: 10 } });
  if (teamsQuery.data && teamsQuery.data.items?.some((t) => t.key && !(t.key in teamCache))) {
    setTeamCache((prev) => ({
      ...prev,
      ...teamsQuery.data.items?.reduce((newEntries, t) => ({ ...newEntries, [t.key ?? '']: t }), {} as TeamCache),
    }));
  }

  function renderOption(option: SelectMembersAndTeamsOption, options?: { compact?: boolean }) {
    return option.kind === 'member' ? (
      <MemberOption memberId={option.value} member={memberCache[option.value]} compact={options?.compact} />
    ) : (
      <TeamOption teamKey={option.value} team={teamsQuery.data?.items?.find((t) => t.key === option.value)} />
    );
  }

  function MultiValue(multiValueProps: MultiValueProps<SelectMembersAndTeamsOption>) {
    return (
      <components.MultiValue {...multiValueProps}>
        {renderOption(multiValueProps.data, { compact: true })}
      </components.MultiValue>
    );
  }

  const memberOptions = membersQuery.data?.items.map((m) => ({ kind: 'member', value: m._id })) ?? [];
  const teamOptions = teamsQuery.data?.items?.map((t) => ({ kind: 'team', value: t.key })) ?? [];

  const isLoading = membersQuery.isPending || teamsQuery.isPending;

  const { valueContainerStyles = {}, controlStyles = {} } = props;

  const customStyles = {
    control: { padding: '0.125rem 0', ...controlStyles },
    valueContainer: {
      maxHeight: '10.625rem',
      overflow: 'auto',
      padding: '0.125rem 0.1875rem',
      ...valueContainerStyles,
    },
    menu: { marginTop: '0' },
    menuList: { paddingTop: '0' },
    multiValue: {
      alignItems: 'center',
      background: 'var(--lp-color-bg-interactive-secondary-hover)',
      borderRadius: '0.5rem',
      height: '1.75rem',
      maxWidth: '32.5rem',
      marginLeft: '0.1875rem',
      paddingLeft: '0.0625rem',
    },
    multiValueLabel: {},
    multiValueRemove: {
      borderRadius: '0 0.5rem 0.5rem 0',
      height: '100%',
      display: props.disabled ? 'none' : 'flex',
    },
    group: {
      paddingBottom: '0',
      paddingTop: '0',
    },
    groupHeading: {
      backgroundColor: 'var(--lp-color-bg-ui-secondary)',
      color: 'var(--lp-color-text-field-placeholder)',
      marginBottom: '0',
      fontSize: '0.875rem',
      textTransform: 'none',
    },
  };

  return (
    <InPortal>
      <CustomSelect
        ariaLabel={props.ariaLabel}
        className={props.className}
        customComponents={{ MultiValue }}
        filterOption={() => true}
        formatOptionLabel={(option: SelectMembersAndTeamsOption) => renderOption(option)}
        isClearable={false}
        isLoading={isLoading}
        isMulti
        name={props.name}
        onBlur={props.onBlur}
        onChange={props.onChange}
        onInputChange={onInputChange}
        options={[...memberOptions, ...teamOptions]}
        placeholder={props.placeholder ?? 'Select members and teams'}
        styles={customStyles}
      />
    </InPortal>
  );
}
